import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatDate, formatDateTime } from '../../utils/format';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';
import SowSelect from '../shared/SowSelect';
import KanbanBoard from '../shared/KanbanBoard';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { ECR_TRACKER_TOUR_ID, ecrTrackerTourSteps } from './ecrTrackerTour';
import {
  LANE_DEFS, LANE_LABELS, LANE_BY_KEY, LANE_GROUPS, LANE_GROUP_LABELS, LANE_SETS, laneLabel, updateText,
  SUBMISSIONS, RESPONSE_LABELS, RR_SLA_WORKING_DAYS, responseTypesFor, slaStatus, slaText, slaResultText,
  correctionWarnings, correctionFieldLabel, responseField,
} from '../../utils/ecrStages';

// ─── ECR Tracker ─────────────────────────────────────────────────────────────
// One card per Rolls-Royce ECR, travelling left to right through the lifecycle.
// The board answers five questions at a glance: where is it, whose is it, how
// long has it been there, what is stopping it, what happens next. Nothing here
// stores ECR paperwork; a card can carry a link to wherever that lives.
//
// No lane is gated. AV review is a lane like any other: when the review is
// done the card is moved on, and the move is what the history records.
//
// Archive is how a card leaves the board, and the only way: there is no
// permanent delete. It is an administrative kill switch rather than a stage,
// so it is offered from every card in every lane, it lives behind the card's
// ... menu with a confirmation behind that, and there is no Archive lane. The
// Archived view is a searchable table beside the board, not a second board.
//
// A card's PM and designer see a gold dot on it, and a count on ECR Tracker
// in the sidebar, when somebody else has changed it since they last opened
// it. Opening the card shows them what changed and clears it for them alone.

const inputCls = 'w-full border border-gray-200 rounded px-2 py-1.5 text-xs bg-white text-gray-800';
const labelCls = 'block text-[10px] font-semibold text-gray-500 uppercase mb-0.5';
const btnPrimary = 'px-3 py-1.5 text-[11px] font-semibold rounded bg-navy text-white hover:opacity-90 disabled:opacity-50';
const btnGold = 'px-3 py-1.5 text-[11px] font-semibold rounded bg-gold text-white hover:opacity-90 disabled:opacity-50';
const btnGhost = 'px-3 py-1.5 text-[11px] font-semibold rounded bg-white border border-gray-200 text-navy hover:bg-gray-50 disabled:opacity-50';
const btnDanger = 'px-3 py-1.5 text-[11px] font-semibold rounded bg-red-600 text-white hover:opacity-90 disabled:opacity-50';
const selectCls = 'border border-gray-200 rounded px-2 py-1 text-xs bg-white text-gray-800';

const personName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim();
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');
// The database stamps datetime('now') in UTC without saying so. Read as UTC,
// so a response entered as 10:15 shows as 10:15 in British Summer Time too.
const utc = (s) => (s ? `${String(s).replace(' ', 'T')}Z` : s);
// A corrected lifecycle date is a bare YYYY-MM-DD: the day it happened, with
// no time to show. Everything else is a UTC stamp.
const isDay = (v) => typeof v === 'string' && v.length === 10;
const dayOf = (v) => (!v ? '' : isDay(v) ? v : new Date(utc(v)).toLocaleDateString('en-CA'));
const showDay = (v) => (v ? formatDate(`${dayOf(v)}T12:00:00`) : '—');
const showWhen = (v) => (isDay(v) ? showDay(v) : formatDateTime(utc(v)));

function Badge({ cls, children, title }) {
  return <span title={title} className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${cls}`}>{children}</span>;
}

// The lane wording for the age: a card sitting with Rolls-Royce is waiting,
// one with us is in stage.
const ageLabel = (card) => (LANE_BY_KEY[card.lane]?.external ? 'Waiting' : 'In stage');
const days = (n) => `${n} day${n === 1 ? '' : 's'}`;

// ─── Card ────────────────────────────────────────────────────────────────────

function EcrCard({ card, selected, onAction }) {
  const updates = card.updates || [];
  const latest = updates[updates.length - 1];
  return (
    <div className={`bg-white border rounded-md p-2 hover:shadow-md transition ${selected ? 'border-gold ring-2 ring-gold/25' : latest ? 'border-gold' : card.blocked ? 'border-red-300' : 'border-gray-200 hover:border-gold'}`}>
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0">
          <div className="text-[10px] font-bold font-mono text-gray-500 truncate flex items-center gap-1">
            {latest && <UpdateDot count={updates.length} />}
            <span className="truncate">{card.project_number}</span>
          </div>
          <div className="text-xs font-semibold text-navy truncate" title={card.project_name || ''}>{card.project_name || 'Untitled project'}</div>
        </div>
        <CardMenu onEdit={() => onAction('edit')} onHistory={() => onAction('history')} onArchive={() => onAction('archive')} />
      </div>
      <div className="mt-1.5 text-[9px] text-gray-500 flex flex-wrap gap-x-2">
        <span>Designer: <span className="text-gray-700 font-semibold">{card.designer_name || '—'}</span></span>
        <span>PM: <span className="text-gray-700 font-semibold">{card.pm_name || '—'}</span></span>
      </div>
      <SlaBadge card={card} />
      <div className="flex items-center justify-between mt-1.5 text-[9px]">
        <span className={card.days_in_stage > 14 ? 'text-amber-700 font-semibold' : 'text-gray-500'}>{ageLabel(card)}: {days(card.days_in_stage)}</span>
        <span className={card.overdue ? 'text-red-700 font-semibold' : 'text-gray-400'}>{card.target_date ? `ECR1/2 Due: ${shortDate(card.target_date)}` : ''}</span>
      </div>
      {(card.blocked || card.overdue) ? (
        <div className="mt-1 flex gap-1">
          {card.blocked ? <Badge cls="bg-red-50 text-red-700" title={card.blocker || ''}>Blocked</Badge> : null}
          {card.overdue ? <Badge cls="bg-blue-50 text-blue-700">Overdue</Badge> : null}
        </div>
      ) : null}
      {card.blocked && card.blocker ? <div className="mt-1 text-[9px] text-red-700 italic truncate" title={card.blocker}>{card.blocker}</div> : null}
      {!card.blocked && card.next_action ? <div className="mt-1 text-[9px] text-gray-500 italic truncate border-l-2 border-gray-200 pl-1.5" title={card.next_action}>{card.next_action}</div> : null}
      {latest && (
        <div className="mt-1.5 rounded bg-gold/10 px-1.5 py-1 text-[9px] text-amber-800 leading-snug" title={updates.map(u => `${u.text} (${u.by_name})`).join('\n')}>
          <div className="font-semibold line-clamp-2">{latest.text}</div>
          <div>{latest.by_name} · {formatDateTime(utc(latest.at))}{updates.length > 1 ? ` · +${updates.length - 1} more` : ''}</div>
        </div>
      )}
    </div>
  );
}

// The mark on a card somebody else has changed since you last opened it. It is
// only ever on cards you are the PM or designer of.
function UpdateDot({ count }) {
  const label = `${count} change${count === 1 ? '' : 's'} since you last opened it`;
  return <span role="img" aria-label={label} title={label} className="inline-block w-2 h-2 rounded-full bg-gold flex-shrink-0" />;
}

// The card's actions. A permanent Delete button on the face of every card
// would be clutter and an accident waiting to happen, so the one action that
// takes a card off the board sits behind the ... with a confirmation behind
// that. The menu swallows its own clicks so opening it does not also select
// the card; dragging stays entirely the shared board's business.
function CardMenu({ onEdit, onHistory, onArchive }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const pick = (fn) => (e) => { e.stopPropagation(); setOpen(false); fn(); };
  const item = 'block w-full text-left px-3 py-1.5 text-[11px] hover:bg-gray-50 whitespace-nowrap';
  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button data-tour="card-menu" aria-label="ECR actions" title="ECR actions"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="px-1 -mr-0.5 -mt-0.5 text-gray-400 hover:text-navy leading-none">&#8942;</button>
      {open && (
        <div className="absolute right-0 top-5 z-20 bg-white border border-gray-200 rounded shadow-lg py-1">
          <button className={`${item} text-navy`} onClick={pick(onEdit)}>Edit ECR</button>
          <button className={`${item} text-navy`} onClick={pick(onHistory)}>View History</button>
          <button className={`${item} text-red-700`} onClick={pick(onArchive)}>Archive ECR</button>
        </div>
      )}
    </div>
  );
}

// Archiving and restoring are both confirmed, and both name the ECR so nobody
// archives the card next to the one they meant.
function ArchiveEcrModal({ card, onArchive, onClose }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSaving(true); setError('');
    try { await onArchive(reason.trim() || null); }
    catch (e) { setError(e.message || 'Could not archive the ECR.'); setSaving(false); }
  };

  return (
    <Modal title={`Archive ${card.project_number}?`} onClose={onClose}
      footer={<>
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={btnDanger} onClick={submit} disabled={saving}>{saving ? 'Archiving...' : 'Archive ECR'}</button>
      </>}>
      <div className="space-y-3">
        <div>
          <div className="text-sm font-bold text-navy">{card.project_name || 'Untitled project'}</div>
          <div className="text-[10px] text-gray-500">Currently {LANE_LABELS[card.lane]}</div>
        </div>
        <div className="text-xs text-gray-600">
          This will remove the ECR from the active Kanban board. The ECR and its history will be retained and can be restored later.
        </div>
        <div>
          <label className={labelCls}>Reason for archiving</label>
          <input className={inputCls} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Optional, e.g. Project cancelled" autoFocus />
        </div>
        {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      </div>
    </Modal>
  );
}

function RestoreEcrModal({ card, onRestore, onClose }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const back = LANE_LABELS[card.archived_from_lane] || LANE_LABELS[card.lane];

  const submit = async () => {
    setSaving(true); setError('');
    try { await onRestore(); }
    catch (e) { setError(e.message || 'Could not restore the ECR.'); setSaving(false); }
  };

  return (
    <Modal title={`Restore ${card.project_number}?`} onClose={onClose}
      footer={<>
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Restoring...' : 'Restore'}</button>
      </>}>
      <div className="space-y-3">
        <div className="text-sm font-bold text-navy">{card.project_name || 'Untitled project'}</div>
        <div className="text-xs text-gray-600">
          This will return the ECR to the active ECR Tracker, at <span className="font-semibold text-navy">{back}</span>. Its stage clock starts again from today.
        </div>
        {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      </div>
    </Modal>
  );
}

// ─── Raise a card ────────────────────────────────────────────────────────────

// Anybody on the staff list can be the designer, the same as the PM. Those
// already designing an ECR on the board come first, so the usual few are not
// lost in the whole company. A designer who has since left is still shown on
// a card that has one, under their name from the card.
function DesignerPicker({ value, onChange, people, designerIds, currentName }) {
  const ids = new Set(designerIds);
  const regulars = people.filter(p => ids.has(p.id));
  const others = people.filter(p => !ids.has(p.id));
  const missing = value && !people.some(p => p.id === value);
  return (
    <select className={inputCls} value={value || ''} onChange={e => onChange(e.target.value || null)}>
      <option value="">Not set</option>
      {missing && <option value={value}>{currentName || 'Former member of staff'}</option>}
      {regulars.length > 0 && (
        <optgroup label="Designing ECRs now">
          {regulars.map(p => <option key={p.id} value={p.id}>{personName(p)}</option>)}
        </optgroup>
      )}
      <optgroup label={regulars.length ? 'Everyone else' : 'Staff'}>
        {others.map(p => <option key={p.id} value={p.id}>{personName(p)}</option>)}
      </optgroup>
    </select>
  );
}

function NewEcrModal({ sows, people, designerIds, onSave, onClose }) {
  const [form, setForm] = useState({ sow_id: '', project_number: '', project_name: '', pm_id: '', designer_id: null, target_date: '', next_action: '' });
  const [noSow, setNoSow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const sow = sows.find(s => String(s.id) === String(form.sow_id));

  const submit = async () => {
    if (!noSow && !form.sow_id) { setError('Pick the SOW, or tick "No SOW yet" and type the project number.'); return; }
    if (noSow && !form.project_number.trim()) { setError('Type the project number.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { designer_id: form.designer_id, target_date: form.target_date || null, next_action: form.next_action || null };
      if (noSow) Object.assign(payload, { project_number: form.project_number.trim(), project_name: form.project_name || null, pm_id: form.pm_id || null });
      else payload.sow_id = form.sow_id;
      await onSave(payload);
    } catch (e) {
      setError(e.message || 'Could not raise the ECR.');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="New ECR" onClose={onClose}
      footer={<>
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Add to board'}</button>
      </>}>
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Project</label>
          {noSow ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="AV-402333" value={form.project_number} onChange={set('project_number')} autoFocus />
              <input className={inputCls} placeholder="Short project name" value={form.project_name} onChange={set('project_name')} />
              <select className={inputCls} value={form.pm_id} onChange={set('pm_id')}>
                <option value="">Project Manager (PM)</option>
                {people.map(p => <option key={p.id} value={p.id}>{personName(p)}</option>)}
              </select>
            </div>
          ) : (
            <>
              <SowSelect sows={sows} value={form.sow_id} onChange={(id) => setForm(f => ({ ...f, sow_id: id }))} placeholder="Search by reference or name" />
              {sow && <div className="text-[10px] text-gray-500 mt-1">{sow.detail || ''}{sow.owner_name ? ` · PM ${sow.owner_name}` : ''}</div>}
            </>
          )}
          <label className="flex items-center gap-1 text-[10px] text-gray-500 mt-1">
            <input type="checkbox" checked={noSow} onChange={e => setNoSow(e.target.checked)} /> No SOW yet, type the project number
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Designer</label>
            <DesignerPicker value={form.designer_id} people={people} designerIds={designerIds}
              onChange={(d) => setForm(f => ({ ...f, designer_id: d }))} />
          </div>
          <div>
            <label className={labelCls}>ECR1/2 Submission Due Date</label>
            <input type="date" className={inputCls} value={form.target_date} onChange={set('target_date')} />
            <div className="text-[9px] text-gray-400 mt-0.5">Date the ECR1/2 needs to be submitted by.</div>
          </div>
          <div>
            <label className={labelCls}>Next action</label>
            <input className={inputCls} value={form.next_action} onChange={set('next_action')} placeholder="e.g. Waiting for final technical input" />
          </div>
        </div>
        {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      </div>
    </Modal>
  );
}

// ─── Card detail ─────────────────────────────────────────────────────────────

function EcrPanel({ cardId, people, sows, designerIds, focus, onClose, onChanged, onSeen, onMove, onArchive }) {
  const { toast } = useToast();
  const [card, setCard] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [linking, setLinking] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  // What changed since this person last opened the card, as it stood when the
  // panel opened. Kept for as long as the panel is open, although opening it
  // has already cleared them, so saving does not wipe what they came to read.
  const [since, setSince] = useState([]);
  const historyRef = useRef(null);

  const load = useCallback(() => api.get(`ecr/${cardId}`).then(c => {
    setCard(c);
    setForm({
      pm_id: c.pm_id || '', designer_id: c.designer_id || null,
      next_action: c.next_action || '', target_date: c.target_date || '',
      blocked: !!c.blocked, blocker: c.blocker || '', notes: c.notes || '', external_url: c.external_url || '',
    });
    return c;
  }).catch(e => { setError(e.message || 'Could not load the ECR.'); return null; }), [cardId]);

  // Opening the card is reading it: its updates are cleared for this person,
  // on the board and in the sidebar count, as soon as it has loaded.
  useEffect(() => {
    let live = true;
    setSince([]);
    load().then(c => {
      if (!live || !c?.updates?.length) return;
      setSince(c.updates);
      api.post(`ecr/${cardId}/seen`, {})
        .then(() => { onSeen?.(cardId); window.dispatchEvent(new CustomEvent('av:ecr-updates-changed')); })
        .catch(() => {});
    });
    return () => { live = false; };
    // onSeen is the page's setter wrapper; re-running on its identity would reload the card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, cardId]);

  // "View History" opens the panel on the history rather than leaving somebody
  // to find it below the fold.
  useEffect(() => {
    if (focus === 'history' && card) historyRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focus, card]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (form.blocked && !form.blocker.trim()) { setError('Say what is blocking it.'); return; }
    setSaving(true); setError('');
    try {
      await api.put(`ecr/${cardId}`, {
        ...form, pm_id: form.pm_id || null, next_action: form.next_action || null, target_date: form.target_date || null,
        blocker: form.blocked ? form.blocker : null, notes: form.notes || null, external_url: form.external_url || null,
      });
      toast('ECR updated', 'success');
      await load(); onChanged?.();
    } catch (e) { setError(e.message || 'Could not save.'); }
    finally { setSaving(false); }
  };

  const linkSow = async (sowId) => {
    if (!sowId) return;
    try { await api.put(`ecr/${cardId}`, { sow_id: sowId }); toast('Linked to the SOW', 'success'); setLinking(false); await load(); onChanged?.(); }
    catch (e) { setError(e.message || 'Could not link the SOW.'); }
  };

  const move = async (lane) => {
    if (!lane || lane === card.lane) return;
    await onMove(card.id, lane);
    await load();
  };

  return (
    <aside data-tour="card-panel" className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white border-l border-gray-200 shadow-2xl z-40 flex flex-col">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <div className="min-w-0">
          <div className="text-[10px] font-bold font-mono text-gray-500">{card?.project_number}</div>
          <h3 className="text-sm font-extrabold text-navy truncate">{card?.project_name || 'Untitled project'}</h3>
          {card && (
            <div className="text-[10px] text-gray-500 mt-0.5">
              PM {card.pm_name || '—'} · {card.sow_reference ? `SOW ${card.sow_reference}` : 'No SOW linked'}
              {!card.sow_id && <button className="ml-1 text-gold hover:underline" onClick={() => setLinking(v => !v)}>{linking ? 'cancel' : 'link one'}</button>}
            </div>
          )}
        </div>
        <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-navy text-lg leading-none flex-shrink-0">×</button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-xs">
        {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        {!card || !form ? <div className="text-gray-400">Loading…</div> : (
          <>
            {since.length > 0 && (
              <div className="border border-gold rounded-lg p-3 bg-gold/10">
                <div className="text-[10px] font-bold uppercase text-gold">Since you last looked</div>
                <ul className="mt-1 space-y-1.5">
                  {since.map(u => (
                    <li key={u.id} className="text-[11px] text-gray-700">
                      {u.text}
                      <span className="block text-[9px] text-gray-500">{u.by_name} · {formatDateTime(utc(u.at))}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-[9px] text-gray-500 mt-2">Cleared for you now you have opened the card. Anyone else named on it still sees it until they open it.</div>
              </div>
            )}
            {linking && <SowSelect sows={sows} value="" onChange={linkSow} placeholder="Search for the SOW" />}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-[10px] font-bold text-gray-500 uppercase">Current position</div>
                  <div className="text-sm font-bold text-navy">{LANE_LABELS[card.lane]}</div>
                  {LANE_BY_KEY[card.lane]?.flag && <div className="text-[9px] font-bold uppercase text-gold">{LANE_BY_KEY[card.lane].flag}</div>}
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-gray-500">{ageLabel(card)}</div>
                  <div className={`text-sm font-bold ${card.days_in_stage > 14 ? 'text-amber-700' : 'text-navy'}`}>{days(card.days_in_stage)}</div>
                </div>
              </div>
              {(card.blocked || card.overdue) ? (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {card.blocked ? <Badge cls="bg-red-50 text-red-700">Blocked</Badge> : null}
                  {card.overdue ? <Badge cls="bg-blue-50 text-blue-700">Overdue</Badge> : null}
                </div>
              ) : null}
              <div className="mt-2">
                <label className={labelCls}>Move to</label>
                <select data-tour="move-select" className={inputCls} value={card.lane} onChange={e => move(e.target.value)}>
                  {LANE_GROUPS.map(g => (
                    <optgroup key={g} label={LANE_GROUP_LABELS[g]}>
                      {LANE_DEFS.filter(l => l.group === g).map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
                    </optgroup>
                  ))}
                </select>
                <div className="text-[9px] text-gray-400 mt-0.5">Dragging the card on the board does the same. Either way the stage clock restarts and the move is recorded.</div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Designer</label>
                <DesignerPicker value={form.designer_id} people={people} designerIds={designerIds} currentName={card.designer_name}
                  onChange={(d) => setForm(f => ({ ...f, designer_id: d }))} />
              </div>
              <div>
                <label className={labelCls}>Project Manager (PM)</label>
                <select className={inputCls} value={form.pm_id} onChange={set('pm_id')}>
                  <option value="">Not set</option>
                  {people.map(p => <option key={p.id} value={p.id}>{personName(p)}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>ECR1/2 Submission Due Date</label>
                <input type="date" className={inputCls} value={form.target_date} onChange={set('target_date')} />
                <div className="text-[9px] text-gray-400 mt-0.5">Date the ECR1/2 needs to be submitted by.</div>
              </div>
              <div>
                <label className={labelCls}>Next action</label>
                <input className={inputCls} value={form.next_action} onChange={set('next_action')} />
              </div>
              <div className="sm:col-span-2 border border-gray-200 rounded p-2 bg-white">
                <label className="flex items-center gap-2 text-xs font-semibold text-navy">
                  <input type="checkbox" checked={form.blocked} onChange={set('blocked')} /> Blocked
                </label>
                {form.blocked && (
                  <input className={`${inputCls} mt-1.5`} value={form.blocker} onChange={set('blocker')} placeholder="What is in the way, e.g. Awaiting technical information from RR" autoFocus />
                )}
                <div className="text-[9px] text-gray-400 mt-1">The card stays in its lane. Blocked says what is stopping it, not where it is.</div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>ECR documents (link)</label>
                <input className={inputCls} value={form.external_url} onChange={set('external_url')} placeholder="https://… folder or document location" />
                {card.external_url && <a href={card.external_url} target="_blank" rel="noreferrer" className="text-[10px] text-gold hover:underline">Open</a>}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notes</label>
                <textarea className={`${inputCls} min-h-[60px]`} value={form.notes} onChange={set('notes')} />
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[9px] text-gray-400">Last updated {formatDateTime(utc(card.updated_at))}{card.updated_by_name ? ` by ${card.updated_by_name}` : ''}</div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button className="text-[10px] font-semibold text-red-700 hover:underline" onClick={() => onArchive(card)}>Archive ECR</button>
                <button className={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
              </div>
            </div>

            <LifecycleSection card={card} onCorrect={setCorrecting} />

            <SlaSection card={card} onChanged={async () => { await load(); onChanged?.(); }} onCorrect={setCorrecting} />

            <div ref={historyRef}>
              <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">History</div>
              <ul className="space-y-1">
                {auditTrail(card).map(({ at, h, c }) => (
                  <li key={(h || c).id} className="text-[11px] text-gray-700 border-l-2 border-gray-200 pl-2">
                    <span className="text-gray-400">{formatDateTime(utc(at))}</span>{' '}
                    {h ? historyLine(h) : correctionLine(c)}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
      {correcting && (
        <CorrectDatesModal card={card} rows={correcting} onClose={() => setCorrecting(null)}
          onSaved={async (n) => {
            setCorrecting(null);
            toast(n === 1 ? 'Lifecycle date corrected' : `${n} lifecycle dates corrected`, 'success');
            await load(); onChanged?.();
          }} />
      )}
    </aside>
  );
}

// Edits to a card's fields, each its own history line since 21 October 2026,
// worded by updateText so the history, the card and the email agree.
const FIELD_EVENTS = new Set(['pm', 'blocked', 'unblocked', 'next_action', 'target_date', 'notes', 'link', 'sow', 'project']);

// One history line. Lane moves, designer assignments, field edits, archive and
// restore sit in one list in time order. Rows written under the retired Internal Review
// approvals are kept and still read as what they were; lanes the board no
// longer has are named by laneLabel.
const RETIRED_EVENTS = {
  pm_review: 'PM Review completed by',
  infrastructure_review: 'Infrastructure Review completed by',
  pm_review_override: 'PM Review approved by override:',
  infrastructure_review_override: 'Infrastructure Review approved by override:',
};

function historyLine(h) {
  const who = <span className="font-semibold">{h.changed_by_name}</span>;
  if (h.event === 'archive') {
    return <>ECR archived by {who}{h.note ? <span className="block text-gray-500">Reason: {h.note}</span> : null}</>;
  }
  if (h.event === 'restore') {
    return <>ECR restored by {who}<span className="block text-gray-500">Returned to: {laneLabel(h.to_lane)}</span></>;
  }
  if (h.event === 'designer') {
    return <>{h.note ? <>Designer assigned: <span className="font-semibold">{h.note}</span></> : 'Designer removed'}<span className="text-gray-400"> · {h.changed_by_name}</span></>;
  }
  if (FIELD_EVENTS.has(h.event)) {
    return <>{updateText(h)}<span className="text-gray-400"> · {h.changed_by_name}</span></>;
  }
  if (h.event === 'rr_submitted' || h.event === 'rr_response') {
    return <>{h.note}<span className="text-gray-400"> · {h.changed_by_name}</span></>;
  }
  if (RETIRED_EVENTS[h.event]) return <>{RETIRED_EVENTS[h.event]} {who}</>;
  if (!h.from_lane) {
    return <>ECR created by {who}<span className="block text-gray-500">Stage: {laneLabel(h.to_lane)}</span></>;
  }
  return <>{laneLabel(h.from_lane)} → {laneLabel(h.to_lane)}<span className="text-gray-400"> · {h.changed_by_name}</span></>;
}

// Lane moves and date corrections in one list, newest first. A correction is
// its own entry beside the move it corrects, never an edit of it, so the list
// says both what happened in BizOps and when it really happened.
function auditTrail(card) {
  return [
    ...card.history.map(h => ({ at: h.changed_at, h })),
    ...(card.corrections || []).map(c => ({ at: c.corrected_at, c })),
  ].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

function correctionLine(c) {
  return (
    <>Lifecycle date corrected by <span className="font-semibold">{c.corrected_by_name}</span>
      <span className="block text-gray-500">Field: {correctionFieldLabel(c.field)} Date</span>
      <span className="block text-gray-500">Previous value: {c.previous_value ? showDay(c.previous_value) : 'Not recorded'}</span>
      <span className="block text-gray-500">New value: {showDay(c.new_value)}</span>
      <span className="block text-gray-500">Reason: {c.reason}</span>
    </>
  );
}

// ─── Lifecycle dates ─────────────────────────────────────────────────────────
// When each step really happened. Moving a card dates the step it enters,
// which is right for an ECR run on the board as it happens and wrong for the
// backlog being entered after the fact; Edit corrects it. The corrected date
// is what the SLA, Days in Stage and every count read from then on, and the
// history keeps the move as well as the correction.

function LifecycleSection({ card, onCorrect }) {
  const rows = card.lifecycle.filter(r => r.reached || r.value);
  const lastFix = (r) => card.corrections.find(c => c.field === r.key && (c.submission_id || null) === (r.submission_id || null));
  return (
    <div data-tour="lifecycle-dates" className="rounded-lg border border-gray-200 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold text-gray-500 uppercase">Lifecycle dates</div>
        <button className="text-[10px] font-semibold text-gold hover:underline" onClick={() => onCorrect(rows)}>Edit lifecycle dates</button>
      </div>
      <div className="text-[9px] text-gray-400">When each step actually happened. Moving the card records the day it moved; correct a date here when the event happened earlier.</div>
      <ul className="divide-y divide-gray-100">
        {rows.map(r => {
          const fix = lastFix(r);
          return (
            <li key={r.key} className="flex items-center justify-between gap-2 py-1">
              <span className="text-[11px] text-gray-600">{r.label}</span>
              <span className="flex items-center gap-2 flex-shrink-0">
                {r.corrected ? (
                  <Badge cls="bg-amber-50 text-amber-700" title={fix ? `Corrected by ${fix.corrected_by_name} on ${showWhen(fix.corrected_at)}: ${fix.reason}` : ''}>Corrected</Badge>
                ) : null}
                <span className={`text-[11px] font-semibold ${r.value ? 'text-navy' : 'text-gray-400'}`}>{r.value ? showDay(r.value) : 'Not recorded'}</span>
                <button className="text-[10px] font-semibold text-gold hover:underline" onClick={() => onCorrect([r])}>Edit</button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// One date (Edit on a row) or the whole lifecycle at once (for a backlog ECR
// whose every date is the day it was entered). A reason is always required,
// and each date that changes is recorded on its own. An unusual date changes
// the dialog rather than failing afterwards: the warning is shown here and
// the button becomes Save Anyway.
const rowId = (r) => `${r.key}|${r.submission_id || ''}`;

function CorrectDatesModal({ card, rows, onClose, onSaved }) {
  const [dates, setDates] = useState(() => Object.fromEntries(rows.map(r => [rowId(r), dayOf(r.value)])));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [serverWarnings, setServerWarnings] = useState([]);
  const single = rows.length === 1 ? rows[0] : null;

  const changes = rows
    .filter(r => dates[rowId(r)] && dates[rowId(r)] !== dayOf(r.value))
    .map(r => ({ field: r.key, submission_id: r.submission_id || null, date: dates[rowId(r)] }));
  const warnings = [...new Set([...correctionWarnings(card.lifecycle, card.submissions, changes, todayIso()), ...serverWarnings])];
  const history = single ? card.corrections.filter(c => c.field === single.key && (c.submission_id || null) === (single.submission_id || null)) : [];

  const setDate = (r) => (e) => { const v = e.target.value; setDates(d => ({ ...d, [rowId(r)]: v })); setServerWarnings([]); };

  const save = async () => {
    if (!changes.length) { setError(single ? 'Choose the date it actually happened.' : 'Change at least one date.'); return; }
    if (!reason.trim()) { setError('Give a reason for the correction.'); return; }
    setSaving(true); setError('');
    try {
      await api.post(`ecr/${card.id}/lifecycle-dates`, { changes, reason: reason.trim(), confirm: warnings.length > 0 });
      await onSaved(changes.length);
    } catch (e) {
      if (e.data?.warnings?.length) setServerWarnings(e.data.warnings);
      else setError(e.message || 'Could not save the correction.');
      setSaving(false);
    }
  };

  return (
    <Modal title={single ? `Correct ${single.label} Date` : 'Edit lifecycle dates'} onClose={onClose} wide={!single}
      footer={<>
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={warnings.length ? btnGold : btnPrimary} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : warnings.length ? 'Save Anyway' : 'Save Correction'}
        </button>
      </>}>
      <div className="space-y-3 text-xs">
        <div className="text-[10px] text-gray-500">{card.project_number} · {card.project_name || 'Untitled project'}</div>
        {single ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className={labelCls}>Current date</div>
              <div className="text-sm font-semibold text-navy">{single.value ? showDay(single.value) : 'Not recorded'}</div>
            </div>
            <div>
              <label className={labelCls}>New date</label>
              <input type="date" className={inputCls} value={dates[rowId(single)]} onChange={setDate(single)} autoFocus />
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead><tr>
              {['Step', 'Current', 'Actual date'].map(h => <th key={h} className="text-left py-1 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={rowId(r)} className="border-t border-gray-100">
                  <td className="py-1 pr-2 text-[11px] text-gray-700">{r.label}</td>
                  <td className="py-1 pr-2 text-[11px] text-gray-500 whitespace-nowrap">{r.value ? showDay(r.value) : 'Not recorded'}</td>
                  <td className="py-1"><input type="date" className={inputCls} value={dates[rowId(r)]} onChange={setDate(r)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div>
          <label className={labelCls}>Reason for correction</label>
          <input className={inputCls} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Historical ECR backlog - actual submission date confirmed from existing records" autoFocus={!single} />
        </div>
        <div className="text-[9px] text-gray-400">
          {single
            ? 'The new date becomes the date this step happened, and the SLA and Days in Stage are worked out from it. The current date stays in the history with who corrected it, when and why.'
            : 'Each date you change is recorded separately in the history, with this reason. Dates you leave alone are not touched.'}
        </div>
        {warnings.length > 0 && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1">
            {warnings.map(w => <div key={w}>Warning: {w}</div>)}
          </div>
        )}
        {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        {history.length > 0 && (
          <div>
            <div className={labelCls}>Earlier corrections</div>
            <ul className="space-y-1">
              {history.map(c => (
                <li key={c.id} className="text-[10px] text-gray-600 border-l-2 border-gray-200 pl-2">
                  {showWhen(c.corrected_at)} · {c.previous_value ? showDay(c.previous_value) : 'Not recorded'} → {showDay(c.new_value)} · {c.corrected_by_name}
                  <span className="block italic text-gray-500">{c.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Rolls-Royce response SLA ────────────────────────────────────────────────
// Rolls-Royce has 10 working days to answer each ECR1/2 and ECR4 submission.
// The server opens the clock when a card enters a submitted lane and closes it
// when the card reaches ECR3/ECR5 or a response is recorded here; the status
// is worked out from the due date on every render, so the countdown moves on
// by itself without anything being written each day.

// The colour of a running SLA, in the badge tints the .dark overrides cover.
const SLA_TONE = {
  within: 'bg-green-50 text-green-700',
  approaching: 'bg-amber-50 text-amber-700',
  due_today: 'bg-orange-100 text-orange-800',
  breached: 'bg-red-50 text-red-700',
};

const todayIso = () => new Date().toLocaleDateString('en-CA');
const cardSla = (c) => (c.sla_due_date ? slaStatus(c.sla_due_date, todayIso()) : null);
const slaTitle = (c) => `Rolls-Royce has ${RR_SLA_WORKING_DAYS} working days to respond to the ${SUBMISSIONS[c.sla_kind]?.label || ''} submission`;

function SlaBadge({ card }) {
  const st = cardSla(card);
  if (!st) return null;
  return (
    <div title={slaTitle(card)} className={`mt-1.5 rounded px-1.5 py-0.5 text-[9px] font-semibold ${SLA_TONE[st.status]}`}>
      {slaText(st)} · Due {shortDate(card.sla_due_date)}
    </div>
  );
}

// datetime-local wants local wall-clock time with no zone.
const localNow = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const correctLink = 'text-[10px] font-semibold text-gold hover:underline';

const resultOf = (s) => ({ within: s.responded_at.slice(0, 10) <= s.due_date, taken: s.working_days_taken, overdue: s.working_days_overdue });

// The panel's SLA section: the running clock with Record RR Response, a way
// to record a formal resubmission from a submitted lane once Rolls-Royce has
// answered, and every submission cycle with its result. Errors are shown
// here, beside the button that was used.
function SlaSection({ card, onChanged, onCorrect }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lanesKind = Object.keys(SUBMISSIONS).find(k => SUBMISSIONS[k].lane === card.lane);
  if (!card.submissions.length && !lanesKind) return null;
  const st = cardSla(card);

  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); setForm(null); await onChanged(); }
    catch (e) { setError(e.message || 'Could not record that.'); }
    finally { setBusy(false); }
  };
  const save = () => {
    if (!form.response_type) { setError('Choose what Rolls-Royce sent.'); return; }
    run(() => api.post(`ecr/${card.id}/rr-response`, {
      response_type: form.response_type,
      responded_at: form.responded_at ? new Date(form.responded_at).toISOString() : null,
      note: form.note.trim() || null,
    }));
  };
  const resubmit = () => run(() => api.post(`ecr/${card.id}/resubmit`, {}));
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div data-tour="rr-sla" className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      <div className="text-[10px] font-bold text-gray-500 uppercase">Rolls-Royce response SLA</div>
      {st ? (
        <div className="space-y-2">
          <div title={slaTitle(card)} className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${SLA_TONE[st.status]}`}>
            {slaText(st)} · Due {formatDate(card.sla_due_date)}
          </div>
          {form ? (
            <div className="border border-gray-200 rounded p-2 bg-white space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Response received</label>
                  <input type="datetime-local" className={inputCls} value={form.responded_at} max={localNow()} onChange={set('responded_at')} />
                </div>
                <div>
                  <label className={labelCls}>Response type</label>
                  <select className={inputCls} value={form.response_type} onChange={set('response_type')} autoFocus>
                    <option value="">Choose…</option>
                    {responseTypesFor(card.sla_kind).map(t => <option key={t} value={t}>{RESPONSE_LABELS[t]}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Note</label>
                  <input className={inputCls} value={form.note} onChange={set('note')} placeholder="Optional, e.g. RFI on cable routes" />
                </div>
              </div>
              <div className="text-[9px] text-gray-400">Any response stops the clock. The card stays where it is: move it when the work moves.</div>
              <div className="flex justify-end gap-2">
                <button className={btnGhost} onClick={() => { setForm(null); setError(''); }}>Cancel</button>
                <button className={btnPrimary} onClick={save} disabled={busy}>{busy ? 'Recording…' : 'Record response'}</button>
              </div>
            </div>
          ) : (
            <button className={btnGold} onClick={() => setForm({ responded_at: localNow(), response_type: '', note: '' })}>Record RR Response</button>
          )}
        </div>
      ) : lanesKind ? (
        <div className="space-y-1.5">
          <div className="text-[10px] text-gray-500">
            {card.submissions.length
              ? `Rolls-Royce has responded to the last submission. When the amended ${SUBMISSIONS[lanesKind].label} goes back to them, record the resubmission to start a new ${RR_SLA_WORKING_DAYS} working day SLA.`
              : `No SLA is running for this card. Record the ${SUBMISSIONS[lanesKind].label} submission to start the ${RR_SLA_WORKING_DAYS} working day clock from today.`}
          </div>
          <button className={btnGhost} onClick={resubmit} disabled={busy}>{busy ? 'Recording…' : card.submissions.length ? 'Record resubmission' : 'Record submission'}</button>
        </div>
      ) : null}
      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {card.submissions.length > 0 && (
        <ul className="space-y-1.5">
          {card.submissions.map(s => (
            <li key={s.id} className="text-[10px] text-gray-600 border-l-2 border-gray-200 pl-2">
              <div>
                <span className="font-semibold text-navy">{SUBMISSIONS[s.kind]?.label} submitted</span> {showWhen(s.submitted_at)}
                {s.submitted_by_name ? ` by ${s.submitted_by_name}` : ''} · Response due {formatDate(s.due_date)}
                {' '}<button className={correctLink} onClick={() => onCorrect([{ key: `${s.kind}_submitted`, label: `${SUBMISSIONS[s.kind].label} Submitted`, value: s.submitted_at, submission_id: s.id }])}>Correct</button>
              </div>
              {s.responded_at ? (
                <div>
                  {RESPONSE_LABELS[s.response_type] || s.response_type} {showWhen(s.responded_at)}{s.responded_by_name ? `, recorded by ${s.responded_by_name}` : ''} ·{' '}
                  <span className={resultOf(s).within ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>{slaResultText(resultOf(s))}</span>
                  {' '}<button className={correctLink} onClick={() => onCorrect([{ key: responseField(s), label: correctionFieldLabel(responseField(s)), value: s.responded_at, submission_id: s.id }])}>Correct</button>
                  {s.response_note ? <span className="block italic text-gray-500">{s.response_note}</span> : null}
                </div>
              ) : <div className="text-gray-400">Awaiting the Rolls-Royce response</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Table view ──────────────────────────────────────────────────────────────

function EcrTable({ cards, onSelect }) {
  const th = 'px-2 py-1.5 text-left text-[9px] font-bold text-gray-500 uppercase border-b border-gray-200 bg-gray-50 whitespace-nowrap';
  const td = 'px-2 py-1.5 border-b border-gray-100 text-[11px] text-gray-700 whitespace-nowrap';
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
      <table className="w-full">
        <thead><tr>
          {['Project', 'Name', 'PM', 'Designer', 'Position', 'Age', 'ECR1/2 Due', 'RR SLA', 'Flags', 'Next action'].map(h => <th key={h} className={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {cards.map(c => (
            <tr key={c.id} onClick={() => onSelect(c)} className="cursor-pointer hover:bg-gray-50">
              <td className={`${td} font-mono font-semibold text-navy`}>
                <span className="inline-flex items-center gap-1">{c.updates?.length ? <UpdateDot count={c.updates.length} /> : null}{c.project_number}</span>
              </td>
              <td className={`${td} max-w-[240px] truncate`} title={c.project_name || ''}>{c.project_name || '—'}</td>
              <td className={td}>{c.pm_name || '—'}</td>
              <td className={td}>{c.designer_name || '—'}</td>
              <td className={td}>{LANE_LABELS[c.lane]}{LANE_BY_KEY[c.lane]?.flag ? <span className="ml-1 text-[9px] font-bold uppercase text-gold">{LANE_BY_KEY[c.lane].flag}</span> : null}</td>
              <td className={`${td} ${c.days_in_stage > 14 ? 'text-amber-700 font-semibold' : ''}`}>{days(c.days_in_stage)}</td>
              <td className={`${td} ${c.overdue ? 'text-red-700 font-semibold' : ''}`}>{c.target_date ? formatDate(c.target_date) : '—'}</td>
              <td className={td}>{cardSla(c) ? <span title={slaTitle(c)} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${SLA_TONE[cardSla(c).status]}`}>{slaText(cardSla(c))}</span> : ''}</td>
              <td className={td}>
                <div className="flex gap-1">
                  {c.blocked ? <Badge cls="bg-red-50 text-red-700" title={c.blocker || ''}>Blocked</Badge> : null}
                  {c.overdue ? <Badge cls="bg-blue-50 text-blue-700">Overdue</Badge> : null}
                </div>
              </td>
              <td className={`${td} max-w-[260px] truncate italic text-gray-500`} title={c.next_action || ''}>{c.blocked ? c.blocker : c.next_action}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {cards.length === 0 && <div className="p-6 text-center text-xs text-gray-400">No ECRs match the current filters</div>}
    </div>
  );
}

// ─── Archived view ───────────────────────────────────────────────────────────
// A searchable table rather than a second board: an archived ECR has no live
// position to drag, only the one it left. Restore is the only action.

function ArchivedTable({ rows, search, onSearch, onRestore, loading }) {
  const th = 'px-2 py-1.5 text-left text-[9px] font-bold text-gray-500 uppercase border-b border-gray-200 bg-gray-50 whitespace-nowrap';
  const td = 'px-2 py-1.5 border-b border-gray-100 text-[11px] text-gray-700 whitespace-nowrap';
  return (
    <div data-tour="archived" className="space-y-2">
      <input className="border border-gray-200 rounded px-3 py-1.5 text-xs bg-white text-gray-800 w-full sm:w-72"
        placeholder="Search archived by project number or name..." value={search} onChange={e => onSearch(e.target.value)} />
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            {['Project', 'Project Name', 'PM', 'Last ECR Position', 'Archived', 'Archived By', 'Reason'].map(h => <th key={h} className={th}>{h}</th>)}
            <th className={th} />
          </tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className={`${td} font-mono font-semibold text-navy`}>{c.project_number}</td>
                <td className={`${td} max-w-[240px] truncate`} title={c.project_name || ''}>{c.project_name || '—'}</td>
                <td className={td}>{c.pm_name || '—'}</td>
                <td className={td}>{c.archived_from_lane ? laneLabel(c.archived_from_lane) : '—'}</td>
                <td className={td}>{c.archived_at ? formatDate(c.archived_at) : '—'}</td>
                <td className={td}>{c.archived_by_name || '—'}</td>
                <td className={`${td} max-w-[260px] truncate italic text-gray-500`} title={c.archive_reason || ''}>{c.archive_reason || '—'}</td>
                <td className={`${td} text-right`}>
                  <button className="text-[10px] font-semibold text-gold hover:underline" onClick={() => onRestore(c)}>Restore</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center text-xs text-gray-400">{search ? 'No archived ECRs match that search' : 'Nothing has been archived'}</div>
        )}
        {loading && <div className="p-6 text-center text-xs text-gray-400">Loading...</div>}
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

const QUICK = [
  { key: 'awaiting_ecr3',    label: 'Awaiting ECR3' },
  { key: 'approved_to_work', label: 'Approved to Work' },
  { key: 'ecr4_required',    label: 'ECR4 Required' },
  { key: 'awaiting_ecr5',    label: 'Awaiting ECR5' },
  { key: 'billable',         label: 'Billable' },
  { key: 'blocked',          label: 'Blocked' },
  { key: 'overdue',          label: 'Overdue' },
  { key: 'sla_active',       label: 'RR SLA Active' },
  { key: 'sla_approaching',  label: 'RR SLA Approaching' },
  { key: 'sla_due_today',    label: 'RR SLA Due Today' },
  { key: 'sla_breached',     label: 'RR SLA Breached' },
];

const GROUPS = LANE_GROUPS.map(g => ({ key: g, label: LANE_GROUP_LABELS[g] }));

export default function EcrTrackerPage({ initialEcrId = null }) {
  const { currentUser } = useApp();
  const { toast } = useToast();
  const [cards, setCards] = useState([]);
  const [people, setPeople] = useState([]);
  const [sows, setSows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [view, setView] = useState('kanban');
  const [search, setSearch] = useState('');
  const [pmFilter, setPmFilter] = useState('');
  const [designerFilter, setDesignerFilter] = useState('');
  const [quick, setQuick] = useState('');
  // A link from the morning email opens the card it names, whether the app is
  // loading fresh or was already on this page.
  const [selectedId, setSelectedId] = useState(initialEcrId);
  useEffect(() => { if (initialEcrId) setSelectedId(initialEcrId); }, [initialEcrId]);
  const [panelFocus, setPanelFocus] = useState(null);
  const [creating, setCreating] = useState(false);
  // Active ECRs or Archived. The board is what opens, always.
  const [tab, setTab] = useState('active');
  const [archived, setArchived] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedSearch, setArchivedSearch] = useState('');
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  // The guided tour opens once per person, after the board has rendered the
  // anchors its steps point at.
  const tour = useFeatureTour(ECR_TRACKER_TOUR_ID, { enabled: !loading && !loadError });

  const load = useCallback(async () => {
    try {
      const rows = await api.get('ecr');
      setCards(Array.isArray(rows) ? rows : []);
      setLoadError('');
    } catch (e) {
      setLoadError(e.message || 'Could not load the ECR Tracker.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // The archived list is a separate query: the board never receives an
  // archived card, so no lane, tile or count has to remember to drop one.
  const loadArchived = useCallback(async () => {
    setArchivedLoading(true);
    try {
      const rows = await api.get('ecr', { archived: 1 });
      setArchived(Array.isArray(rows) ? rows : []);
      setLoadError('');
    } catch (e) {
      setLoadError(e.message || 'Could not load the archived ECRs.');
    } finally { setArchivedLoading(false); }
  }, []);
  useEffect(() => { if (tab === 'archived') loadArchived(); }, [tab, loadArchived]);

  useEffect(() => {
    api.get('people').then(p => setPeople((Array.isArray(p) ? p : []).filter(x => x.employee_status !== 'left'))).catch(() => {});
    api.get('sows').then(s => setSows(Array.isArray(s) ? s : [])).catch(() => {});
  }, []);

  const pms = useMemo(() => {
    const m = new Map();
    for (const c of cards) if (c.pm_id && c.pm_name) m.set(c.pm_id, c.pm_name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [cards]);
  // The designers on the board, for the filter and to head the designer picker.
  const designers = useMemo(() => {
    const m = new Map();
    for (const c of cards) if (c.designer_id && c.designer_name) m.set(c.designer_id, c.designer_name);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [cards]);
  const designerIds = useMemo(() => designers.map(([id]) => id), [designers]);
  const updatedCount = cards.filter(c => c.updates?.length).length;

  // A card just opened has been read: its dot goes at once, without waiting
  // for the board to reload.
  const cardSeen = useCallback((id) => {
    setCards(cs => cs.map(c => (c.id === id ? { ...c, updates: [] } : c)));
  }, []);

  const quickMatch = (c, key) => {
    if (!key) return true;
    if (key === 'mine') return c.pm_id === currentUser?.id;
    if (key === 'updated') return !!c.updates?.length;
    if (key === 'blocked') return !!c.blocked;
    if (key === 'overdue') return !!c.overdue;
    if (key === 'sla_active') return !!c.sla_due_date;
    if (key.startsWith('sla_')) return cardSla(c)?.status === key.slice(4);
    if (LANE_SETS[key]) return LANE_SETS[key].includes(c.lane);
    return true;
  };

  const q = search.trim().toLowerCase();
  const visible = cards.filter(c =>
    (!q || [c.project_number, c.project_name, c.pm_name, c.designer_name, c.sow_reference].some(v => (v || '').toLowerCase().includes(q))) &&
    (!pmFilter || c.pm_id === pmFilter) &&
    (!designerFilter || c.designer_id === designerFilter) &&
    quickMatch(c, quick));

  const tiles = [
    { key: '', label: 'Active ECRs', value: cards.length },
    { key: 'awaiting_ecr3', label: 'Awaiting ECR3', value: cards.filter(c => quickMatch(c, 'awaiting_ecr3')).length, hint: 'Cannot start work' },
    { key: 'approved_to_work', label: 'Approved to Work', value: cards.filter(c => quickMatch(c, 'approved_to_work')).length },
    { key: 'ecr4_required', label: 'ECR4 Required', value: cards.filter(c => quickMatch(c, 'ecr4_required')).length },
    { key: 'awaiting_ecr5', label: 'Awaiting ECR5', value: cards.filter(c => quickMatch(c, 'awaiting_ecr5')).length, hint: 'Cannot yet bill' },
    { key: 'billable', label: 'ECR5 Received / Billable', value: cards.filter(c => quickMatch(c, 'billable')).length },
    { key: 'blocked', label: 'Blocked', value: cards.filter(c => c.blocked).length },
    { key: 'overdue', label: 'Overdue', value: cards.filter(c => c.overdue).length, hint: 'The ECR1/2 submission due date has passed and the ECR1/2 is not yet submitted' },
    { key: 'sla_breached', label: 'RR SLA Breached', value: cards.filter(c => quickMatch(c, 'sla_breached')).length, hint: 'ECR1/2 and ECR4 submissions Rolls-Royce has not answered within 10 working days' },
  ];

  const moveCard = async (id, lane) => {
    const c = cards.find(x => x.id === id);
    if (!c || c.lane === lane) return;
    // Optimistic: move the card, then confirm with the server.
    setCards(cs => cs.map(x => x.id === id ? { ...x, lane, days_in_stage: 0 } : x));
    try {
      await api.post(`ecr/${id}/move`, { lane });
      toast(`${c.project_number} moved to ${LANE_LABELS[lane]}`, 'success');
    } catch (e) {
      toast(e.message || 'Could not move the ECR', 'error');
    }
    await load();
  };

  // Archiving is allowed from any lane, so there is no
  // check here: the confirmation is the whole of the ceremony. The failure, if
  // there is one, is shown in the dialog the person is standing in.
  const archiveCard = async (card, reason) => {
    await api.post(`ecr/${card.id}/archive`, { reason });
    setArchiveTarget(null);
    if (selectedId === card.id) setSelectedId(null);
    toast(`${card.project_number} archived`, 'success');
    await load();
    if (tab === 'archived') await loadArchived();
  };

  const restoreCard = async (card) => {
    await api.post(`ecr/${card.id}/restore`, {});
    setRestoreTarget(null);
    toast(`${card.project_number} restored to ${LANE_LABELS[card.archived_from_lane] || 'the board'}`, 'success');
    await loadArchived();
    await load();
  };

  const cardAction = (card, kind) => {
    if (kind === 'archive') { setArchiveTarget(card); return; }
    setPanelFocus(kind === 'history' ? 'history' : null);
    setSelectedId(card.id);
  };

  const create = async (payload) => {
    const c = await api.post('ecr', payload);
    toast(`${c.project_number} added to the board`, 'success');
    setCreating(false);
    await load();
    setSelectedId(c.id);
  };

  const aq = archivedSearch.trim().toLowerCase();
  const archivedVisible = archived.filter(c =>
    !aq || [c.project_number, c.project_name].some(v => (v || '').toLowerCase().includes(aq)));

  const clearFilters = () => { setSearch(''); setPmFilter(''); setDesignerFilter(''); setQuick(''); };
  const filtering = q || pmFilter || designerFilter || quick;

  const chip = (on) => `px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${on ? 'bg-navy text-white border-gold' : 'bg-white text-gray-500 border-gray-200 hover:border-gold'}`;

  return (
    <div className="fade-in">
      <div data-tour="page-header" className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy flex items-center gap-1.5">
            ECR Tracker
            <button onClick={tour.start} aria-label="Replay the guided tour of this page" title="Replay the guided tour of this page"
              className="w-4 h-4 rounded-full border border-gray-200 bg-white text-gray-500 text-[9px] font-bold leading-none hover:border-gold hover:text-navy">?</button>
          </h2>
          <div className="text-[9px] text-gray-500">Every Rolls-Royce ECR as a card: where it is, whose it is, how long it has been there and what is stopping it.</div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <div data-tour="archive-toggle" className="flex rounded overflow-hidden border border-gray-200">
            <button onClick={() => setTab('active')} className={`px-3 py-1.5 text-[11px] font-semibold ${tab === 'active' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Active ECRs</button>
            <button onClick={() => setTab('archived')} className={`px-3 py-1.5 text-[11px] font-semibold ${tab === 'archived' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Archived</button>
          </div>
          {tab === 'active' && (
            <div data-tour="view-toggle" className="flex rounded overflow-hidden border border-gray-200">
              <button onClick={() => setView('kanban')} className={`px-3 py-1.5 text-[11px] font-semibold ${view === 'kanban' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Kanban</button>
              <button onClick={() => setView('table')} className={`px-3 py-1.5 text-[11px] font-semibold ${view === 'table' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Table</button>
            </div>
          )}
          <button data-tour="new-ecr" className={btnGold} onClick={() => setCreating(true)}>+ New ECR</button>
        </div>
      </div>

      {loadError && <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{loadError}</div>}

      {tab === 'archived' ? (
        <ArchivedTable rows={archivedVisible} search={archivedSearch} onSearch={setArchivedSearch}
          onRestore={setRestoreTarget} loading={archivedLoading} />
      ) : (
      <>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <input data-tour="search" className="border border-gray-200 rounded px-3 py-1.5 text-xs bg-white text-gray-800 w-full sm:w-72"
          placeholder="Search project number, name, PM or designer…" value={search} onChange={e => setSearch(e.target.value)} />
        <div data-tour="filters" className="flex flex-wrap items-center gap-1.5">
          <select className={selectCls} value={pmFilter} onChange={e => setPmFilter(e.target.value)}>
            <option value="">All PMs</option>
            {pms.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select className={selectCls} value={designerFilter} onChange={e => setDesignerFilter(e.target.value)}>
            <option value="">All designers</option>
            {designers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          {filtering && <button className="text-[10px] text-gold hover:underline" onClick={clearFilters}>Clear</button>}
        </div>
      </div>

      <div data-tour="quick-filters" className="flex flex-wrap gap-1 mb-3">
        <button className={chip(quick === 'mine')} onClick={() => setQuick(quick === 'mine' ? '' : 'mine')}>My Projects</button>
        <button className={chip(quick === 'updated')} onClick={() => setQuick(quick === 'updated' ? '' : 'updated')}
          title="ECRs you are PM or designer on that somebody else has changed since you last opened them">
          Updated for me{updatedCount ? ` (${updatedCount})` : ''}
        </button>
        {QUICK.map(f => (
          <button key={f.key} className={chip(quick === f.key)} onClick={() => setQuick(quick === f.key ? '' : f.key)}>{f.label}</button>
        ))}
      </div>

      <div data-tour="tiles" className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-9 gap-2 mb-3">
        {tiles.map(t => (
          <button key={t.label} onClick={() => setQuick(t.key)} title={t.hint || ''}
            className={`text-left bg-white border rounded-lg px-3 py-2 hover:border-gold transition ${quick === t.key ? 'border-gold ring-2 ring-gold/25' : 'border-gray-200'}`}>
            <div className={`text-xl font-bold leading-tight ${t.key === 'blocked' && t.value ? 'text-red-700' : t.key === 'overdue' && t.value ? 'text-blue-700' : t.key === 'sla_breached' && t.value ? 'text-red-700' : 'text-navy'}`}>{t.value}</div>
            <div className="text-[10px] text-gray-500">{t.label}</div>
          </button>
        ))}
      </div>

      {loading ? <div className="text-xs text-gray-400">Loading…</div> : cards.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-xs text-gray-500">
          <div className="text-sm font-semibold text-navy mb-1">No ECRs on the board yet</div>
          Add the first card and drag it along as the ECR moves.
          <div className="mt-3"><button className={btnPrimary} onClick={() => setCreating(true)}>+ New ECR</button></div>
        </div>
      ) : (
        <div data-tour="board">
          {view === 'table' ? (
            <EcrTable cards={visible} onSelect={(c) => setSelectedId(c.id)} />
          ) : (
            <KanbanBoard lanes={LANE_DEFS} groups={GROUPS} items={visible} laneOf={c => c.lane}
              onMove={moveCard} onSelect={(c) => setSelectedId(c.id)} selectedKey={selectedId} laneWidth={200}
              focusKey={`${q}|${pmFilter}|${designerFilter}|${quick}`}
              emptyLabel={filtering ? 'None match' : 'Empty'}
              renderCard={(c, { selected }) => <EcrCard card={c} selected={selected} onAction={(kind) => cardAction(c, kind)} />} />
          )}
        </div>
      )}

      </>
      )}

      {creating && <NewEcrModal sows={sows} people={people} designerIds={designerIds} onSave={create} onClose={() => setCreating(false)} />}
      {selectedId && (
        <EcrPanel cardId={selectedId} people={people} sows={sows} designerIds={designerIds} focus={panelFocus}
          onClose={() => { setSelectedId(null); setPanelFocus(null); }} onChanged={load} onSeen={cardSeen} onMove={moveCard}
          onArchive={(c) => setArchiveTarget(c)} />
      )}
      {archiveTarget && (
        <ArchiveEcrModal card={archiveTarget} onClose={() => setArchiveTarget(null)}
          onArchive={(reason) => archiveCard(archiveTarget, reason)} />
      )}
      {restoreTarget && (
        <RestoreEcrModal card={restoreTarget} onClose={() => setRestoreTarget(null)}
          onRestore={() => restoreCard(restoreTarget)} />
      )}
      {tour.open && <FeatureTour steps={ecrTrackerTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
